import { Check, X } from 'lucide-react';

interface ProgressStepperProps {
  status: 'claimed' | 'submitted' | 'approved' | 'rejected';
}

const steps = [
  { key: 'claimed', label: 'Claimed' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'approved', label: 'Approved' },
] as const;

function getStepState(
  stepKey: string,
  status: string
): 'completed' | 'active' | 'rejected' | 'pending' {
  const order = ['claimed', 'submitted', 'approved'];
  const statusIndex = order.indexOf(status === 'rejected' ? 'submitted' : status);
  const stepIndex = order.indexOf(stepKey);

  if (status === 'rejected' && stepKey === 'approved') return 'rejected';
  if (stepIndex < statusIndex) return 'completed';
  if (stepIndex === statusIndex) return status === 'rejected' ? 'completed' : 'active';
  return 'pending';
}

export function ProgressStepper({ status }: ProgressStepperProps) {
  return (
    <div className="flex items-center w-full max-w-md">
      {steps.map((step, index) => {
        const state = getStepState(step.key, status);

        return (
          <div key={step.key} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              <div
                className={`relative w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${
                  state === 'completed'
                    ? 'bg-green-500 text-white'
                    : state === 'active'
                    ? 'bg-primary-500 text-white ring-4 ring-primary-100'
                    : state === 'rejected'
                    ? 'bg-red-500 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {state === 'completed' ? (
                  <Check className="w-4 h-4" />
                ) : state === 'rejected' ? (
                  <X className="w-4 h-4" />
                ) : (
                  index + 1
                )}
              </div>
              <span
                className={`mt-1 text-xs font-medium ${
                  state === 'completed'
                    ? 'text-green-600'
                    : state === 'active'
                    ? 'text-primary-600'
                    : state === 'rejected'
                    ? 'text-red-600'
                    : 'text-gray-400'
                }`}
              >
                {state === 'rejected' ? 'Rejected' : step.label}
              </span>
            </div>

            {index < steps.length - 1 && (
              <div className="flex-1 mx-2 mb-5">
                <div
                  className={`h-0.5 transition-all duration-300 ${
                    getStepState(steps[index + 1].key, status) !== 'pending'
                      ? 'bg-green-500'
                      : state === 'active' || state === 'completed'
                      ? 'bg-gradient-to-r from-green-500 to-gray-200'
                      : 'bg-gray-200'
                  }`}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
